import React, { useMemo } from 'react'
import {
  Button,
  Heading,
  Paragraph,
  Popup,
  Select,
  Field,
  DateInput,
  Checkbox,
  Label,
  ArrowUpRightFromSquare,
  VialCircleCheckIcon,
  StarIcon,
  UploadIcon,
  FileXmarkIcon,
  PaperClipIcon,
  TrashIcon,
  ErrorMessage,
  showToast,
} from '@quickmd-llc/design-system'
import moment from 'moment'
import {
  type PatientDrugScreenStatusStaffApiOutContract,
  type PatientStaffApiOutContract,
} from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { DrugScreenBadge } from './StatusBadge'
import { Controller, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import {
  useGetPatientsPatientIdMediafilesFileId,
  usePutPatientsPatientIdMediafilesFileId,
  usePostPatientsPatientIdMediafiles,
  getPatientsPatientIdMediafilesFileId,
  getGetPatientsPatientIdMediafilesFileIdQueryKey,
} from 'api/generated/media-file/media-file'
import { getPatientsPatientId, getGetPatientsPatientIdQueryKey } from 'api/generated/patient/patient'
import { client } from 'api/components/queryClient'
import { Link } from 'react-router-dom'
import { TAG_OPTIONS } from 'pages/charts/patient-chart/Constants'
import { capitalizeFirstLetter, getState } from 'utils/common'
import { useGetSettings } from 'api/generated/setting/setting'
import { getFileExtension, isFileTypeSupported } from '@quickmd-llc/frontend-utils'
import { ALLOWED_FILE_EXTENSIONS_STR } from 'constants/fileUpload'
import { DrugScreenRequirements } from './DrugScreenRequirements'
import { showApiErrorToast } from 'utils/showApiErrorToast'
import { ADD_MEDIA_FILE_SUCCESS } from 'store/charts/ChartsConstants'

type DrugScreenResponse = {
  FileId: string
  Tags: string[]
  DrugScreen?: {
    CompleteDate: string
    ExpireDate: string
    Status: 'verified' | 'pending' | 'rejected' | 'expired' | 'notrequired' | 'review'
  }
  Author: {
    UserType: 'patient' | 'staff' | 'doctor'
    UserId: string
    FirstName: string
    LastName: string
    Prefix: string | null
    Suffix: string | null
  }
  FileName: string
  Url: string
  UploadDateTime: string
}

type FormValues = {
  tag: string
  completedOn?: string
  expiresOn?: string
  expires: boolean
}
export type DrugScreenView = 'empty' | 'upload' | 'onFile'
type Props = {
  isOpen: boolean
  onClose: () => void
  view: DrugScreenView
  drugScreen?: PatientDrugScreenStatusStaffApiOutContract
  patient?: Partial<PatientStaffApiOutContract>
  onStartUpload?: () => void
  // The picked file is owned by VisitPatientInformation so it survives view flips.
  pickedFile?: File
  onClearFile?: () => void
  // Called once the POST resolves and the new FileId is known, so the parent can clear
  // pickedFile and flip view to 'onFile'.
  onUploaded?: (fileId: string) => void
  // The hidden <input type="file"> itself is rendered here (inside the Popup's DialogPanel),
  // even though its ref/state/validation are owned by VisitPatientInformation above the modal.
  // This isn't cosmetic: Headless UI's Dialog treats any pointer interaction with an element
  // outside the panel as an "outside click" and auto-closes — confirmed by a failing test where
  // firing a native file-picker interaction on a sibling-of-the-modal hidden input closed the
  // popup immediately. Keeping the DOM node inside the panel (while state stays above it) avoids
  // that without fighting Headless UI's internals.
  fileInputRef?: React.RefObject<HTMLInputElement>
  onFileChosen?: (e: React.ChangeEvent<HTMLInputElement>) => void
}

// No DS Skeleton/formatting helper exists for this (see the confirmed-symbols note above) —
// small local helper for the attached-file card's size label.
const formatFileSize = (bytes?: number): string => {
  if (bytes === undefined) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export const DrugScreenModal: React.FC<Props> = ({
  isOpen,
  onClose,
  view,
  drugScreen,
  patient,
  onStartUpload,
  pickedFile,
  onClearFile,
  onUploaded,
  fileInputRef,
  onFileChosen,
}) => {
  const dispatch = useDispatch()
  const formatTagLabel = (value: string) =>
    value.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
  const settings = useGetSettings({ query: { enabled: false } }).data as unknown as {
    SettingsPerState: { StateCode: string; DrugScreenHtml: string }[]
  }
  // State cadence settings are seeded into the react-query cache at app startup; until that
  // seeding lands, `settings` is undefined. This query is intentionally disabled (we only ever
  // read from cache), so React Query's own isLoading always reports false here — derive our own
  // loading signal from whether the cached data has actually arrived.
  const isRequirementsLoading = !settings?.SettingsPerState
  const stateRules = useMemo(
    () =>
      settings?.SettingsPerState?.find((stateSetting) => stateSetting.StateCode === patient?.Address?.StateCode)
        ?.DrugScreenHtml,
    [settings, patient?.Address?.StateCode],
  )
  // getState returns the matched { state, code } object or '' — narrow with && (not ?.) so the
  // empty-string branch is excluded before reading .state.
  const matchedState = getState(patient?.Address?.StateCode)
  const stateName = (matchedState && matchedState.state) || patient?.Address?.StateCode || '—'
  const { data: mediaFileData } = useGetPatientsPatientIdMediafilesFileId(
    // @ts-expect-error TS2769 -- MIGRATION: not approved, fix when encountered
    patient?.PatientId,
    patient?.DrugScreen?.FileId,
    {
      query: { enabled: false },
    },
  )
  const { FileName = '', Url, Author, UploadDateTime, FileId } = (mediaFileData as unknown as DrugScreenResponse) || {}
  const isPDF = getFileExtension(FileName) === 'pdf'

  const { control, handleSubmit, watch, register, formState, setValue } = useForm<FormValues>({
    // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
    defaultValues: {
      tag: 'DrugScreen',
      completedOn: drugScreen?.CompleteDate ? moment(drugScreen?.CompleteDate).format('YYYY-MM-DD') : '',
      expires: !!drugScreen?.ExpireDate,
      expiresOn: drugScreen?.ExpireDate ? moment(drugScreen.ExpireDate).format('YYYY-MM-DD') : undefined,
    },
    mode: 'onChange',
  })
  const { errors } = formState
  const expiresOnError = errors.expiresOn

  const expires = watch('expires')
  const uploadedLabel = useMemo(() => {
    // Until the media file resolves (e.g. the onFile preview-loading window) UploadDateTime is
    // undefined; moment(undefined) formats as "now", so guard it rather than show today's date.
    if (!UploadDateTime) return '—'
    const d = moment(UploadDateTime).format('MMMM D YYYY')
    return `${d} by ${capitalizeFirstLetter(Author?.UserType || 'unknown')}`
  }, [UploadDateTime, Author?.UserType])

  const putMediaFileMutation = usePutPatientsPatientIdMediafilesFileId({
    mutation: {
      onSuccess: (response, variables) => {
        // @ts-expect-error TS6133 -- MIGRATION: not approved, fix when encountered
        const { patientId, fileId, data } = variables
        const mediaFileQueryKey = getGetPatientsPatientIdMediafilesFileIdQueryKey(patientId, fileId)
        client.setQueryData(mediaFileQueryKey, response)
      },
    },
  })

  const handleSave = handleSubmit(async (data) => {
    try {
      const patientId = patient?.PatientId
      const fileId = FileId

      const payload = {
        Tags: data.tag ? [data.tag] : undefined,
        DrugScreen: {
          CompleteDate: data.completedOn || null,
          ExpireDate: data.expires ? data.expiresOn || null : null,
        },
      }

      // @ts-expect-error TS2375 -- MIGRATION: not approved, fix when encountered
      await putMediaFileMutation.mutateAsync({ patientId: patientId!, fileId: fileId, data: payload })

      // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
      const mediaFileQueryKey = getGetPatientsPatientIdMediafilesFileIdQueryKey(patientId, fileId)
      try {
        client.invalidateQueries({ queryKey: mediaFileQueryKey })
      } catch (e) {
        console.error('Failed to invalidate media file query:', e)
      }

      // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
      const patientQueryKey = getGetPatientsPatientIdQueryKey(patientId)
      // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
      const updatedPatient = await getPatientsPatientId(patientId)
      client.setQueryData(patientQueryKey, updatedPatient)

      showToast({ type: 'success', message: 'File updated.' })

      onClose()
    } catch (error) {
      showToast({ type: 'error', message: 'Failed to save drug screen data.' })
    }
  })
  const postMediaFile = usePostPatientsPatientIdMediafiles()

  const handleUpload = handleSubmit(async (data) => {
    const patientId = patient?.PatientId
    if (!patientId || !pickedFile) return
    try {
      // Body per PostPatientsPatientIdMediafilesBody (confirmed Task 1) — the Orval mutator
      // builds the multipart FormData from these fields, we don't hand-build it.
      await postMediaFile.mutateAsync({
        patientId,
        data: {
          file: pickedFile,
          tags: [data.tag],
          drugScreenCompleteDate: data.completedOn || undefined,
          drugScreenExpireDate: data.expires ? data.expiresOn || undefined : undefined,
        },
      })

      // The POST response is void (confirmed Task 1) — re-fetch the patient to learn the new
      // DrugScreen.FileId, then fetch that media file to seed the onFile preview cache and keep
      // the legacy Redux charts list in sync via the existing ADD_MEDIA_FILE_SUCCESS reducer.
      const patientQueryKey = getGetPatientsPatientIdQueryKey(patientId)
      const updatedPatient = await getPatientsPatientId(patientId)
      client.setQueryData(patientQueryKey, updatedPatient)

      const newFileId = updatedPatient?.DrugScreen?.FileId
      showToast({ type: 'success', message: 'Drug screen uploaded.' })

      if (newFileId) {
        // The re-fetched patient already carries the new FileId — seed the media-file cache,
        // keep the legacy Redux charts list in sync, and flip the parent to the onFile view.
        const newFile = await getPatientsPatientIdMediafilesFileId(patientId, newFileId)
        client.setQueryData(getGetPatientsPatientIdMediafilesFileIdQueryKey(patientId, newFileId), newFile)
        dispatch({ type: ADD_MEDIA_FILE_SUCCESS, payload: newFile, extraData: { patientId } })
        onUploaded?.(newFileId)
      } else {
        // POST succeeded server-side but the re-fetch hasn't surfaced DrugScreen.FileId yet.
        // Don't flip to onFile with an undefined fileId (that would render a half-populated edit
        // view whose Save would PUT a malformed request) — just close cleanly; the row/chart will
        // reflect the file on the next natural refresh.
        onClose()
      }
    } catch (error) {
      showApiErrorToast(error, { fallback: 'Failed to upload the drug screen. Please try again.' })
    }
  })

  const getTarget = () => (!isFileTypeSupported(FileName) ? '_self' : '_blank')

  if (!isOpen) return null

  // Shared Completed On / Expires / Expires On fields — used by both the upload view and the
  // onFile (edit) view so the markup isn't duplicated.
  const renderDateFields = () => (
    <>
      <div className='flex flex-col gap-sp-1'>
        <Label>Completed On</Label>
        <Field>
          <Controller
            name='completedOn'
            control={control}
            render={({ field }) => (
              <div className='relative flex items-center'>
                <DateInput
                  className='[&+span]:flex [&+span]:h-[24px] [&+span]:items-center'
                  value={field.value ?? ''}
                  placeholder='mm/dd/yyyy'
                  max={moment().format('YYYY-MM-DD')}
                  onChange={(value) => field.onChange(value || undefined)}
                />
              </div>
            )}
          />
        </Field>
      </div>

      <Field>
        <div className='flex items-center gap-sp-1'>
          <Controller
            name='expires'
            control={control}
            render={({ field: { onChange, value } }) => (
              <Checkbox
                onChange={(checked: boolean) => {
                  onChange(checked)
                  if (!checked) {
                    setValue('expiresOn', undefined, { shouldDirty: true, shouldValidate: true })
                  }
                }}
                checked={!!value}
              />
            )}
          />
          <Label className='text-xs'>Expires</Label>
        </div>
      </Field>

      {expires ?
        <div className='flex flex-col gap-sp-1'>
          <Label>Expires On</Label>
          <Field error={!!expiresOnError}>
            {expiresOnError?.message ?
              <ErrorMessage>{expiresOnError?.message}</ErrorMessage>
            : null}
            <Controller
              name='expiresOn'
              control={control}
              render={({ field }) => (
                <DateInput
                  className='[&+span]:flex [&+span]:h-[24px] [&+span]:items-center'
                  value={field.value ?? ''}
                  placeholder='mm/dd/yyyy'
                  onChange={(value) => field.onChange(value || undefined)}
                  min={moment().format('YYYY-MM-DD')}
                />
              )}
              rules={{
                required: {
                  value: true,
                  message: 'Expires On is required when Expires is checked.',
                },
              }}
            />
          </Field>
        </div>
      : null}
    </>
  )

  const renderOnFileView = () => (
    <form className='flex w-full max-w-3xl flex-col gap-sp-4' onSubmit={handleSave}>
      <div className='flex w-[230px] flex-col items-center gap-sp-1'>
        <div className='flex w-full items-center justify-between'>
          <div className='flex items-center'>
            <StarIcon iconSize='xs' />
            <Paragraph size='xs' className='ml-sp-0.25 font-semibold text-text-dark'>
              Status
            </Paragraph>
          </div>
          {/*
               // @ts-expect-error TS2375 -- MIGRATION: not approved, fix when encountered */}
          <DrugScreenBadge drugScreen={drugScreen} />
        </div>
        <div className='flex w-full items-center justify-between'>
          <div className='flex items-center'>
            <UploadIcon iconSize='xs' />
            <Paragraph size='xs' className='ml-sp-0.25 font-semibold text-text-dark'>
              Uploaded
            </Paragraph>
          </div>
          <Paragraph size='xs' className='text-text-light'>
            {uploadedLabel}
          </Paragraph>
        </div>
      </div>

      <div className='flex flex-col gap-sp-3'>
        <div className='grid grid-cols-2 gap-sp-3'>
          {Url ?
            <div className='relative'>
              <Link className='absolute right-sp-2 top-sp-2' to={Url} target={getTarget()}>
                <Button size='s' priority='secondary' isIcon>
                  <ArrowUpRightFromSquare />
                </Button>
              </Link>
              <div className='flex h-full w-full flex-1 overflow-hidden rounded-lg border-b border-border-medium'>
                {isPDF ?
                  <object className='' data={Url} type='application/pdf' width='100%' height='100%'>
                    <Paragraph>{FileName} preview</Paragraph>
                  </object>
                : <img className='w-full' src={Url} alt={`${FileName} preview`} />}
              </div>
            </div>
          : patient?.DrugScreen?.FileId ?
            // A file is expected (the patient record already has a DrugScreen.FileId) but the
            // media-file GET for it hasn't resolved a Url yet — most commonly right after a
            // fresh upload flips this modal to 'onFile'. Show a skeleton instead of an empty
            // frame. No DS Skeleton primitive exists (confirmed above) — Tailwind animate-pulse
            // with the same bg-border-light token used by DrugScreenRequirements' loading state.
            <div
              data-testid='preview-loading'
              className='h-full min-h-[200px] w-full animate-pulse rounded-lg border-b border-border-medium bg-border-light'
            />
          : null}
          <div className='flex flex-col gap-sp-2'>
            <div className='flex flex-col gap-sp-1'>
              <Paragraph className='font-semibold text-text-dark'>Tags</Paragraph>
              <Field>
                <Select {...register('tag')}>
                  {TAG_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {formatTagLabel(opt.value)}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            {renderDateFields()}
            <DrugScreenRequirements
              stateName={stateName}
              stateRulesHtml={stateRules}
              isLoading={Boolean(isRequirementsLoading)}
            />
          </div>
        </div>

        <div className='mt-sp-3 flex justify-end gap-sp-2'>
          <Button priority='primary' type='submit' loading={putMediaFileMutation.isPending}>
            Save
          </Button>
        </div>
      </div>
    </form>
  )

  const renderUploadView = () => (
    <form className='flex w-full flex-col gap-sp-4' onSubmit={handleUpload}>
      <div className='flex items-center gap-sp-1 rounded-lg border border-border-medium p-sp-2'>
        <PaperClipIcon iconSize='l' className='text-text-light' />
        <div className='flex flex-1 flex-col'>
          <Paragraph className='font-semibold text-text-dark'>{pickedFile?.name}</Paragraph>
          <Paragraph size='xs' className='text-text-light'>
            {formatFileSize(pickedFile?.size)} • {postMediaFile.isPending ? 'Uploading…' : 'Ready'}
          </Paragraph>
        </div>
        <Button size='s' priority='tertiary' isIcon onClick={onClearFile} disabled={postMediaFile.isPending}>
          <TrashIcon />
        </Button>
      </div>

      <div className='flex flex-col gap-sp-1'>
        <Paragraph className='font-semibold text-text-dark'>Tags</Paragraph>
        <Field>
          <Select {...register('tag')}>
            {TAG_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {formatTagLabel(opt.value)}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {renderDateFields()}
      <DrugScreenRequirements
        stateName={stateName}
        stateRulesHtml={stateRules}
        isLoading={Boolean(isRequirementsLoading)}
      />

      <div className='mt-sp-2 flex justify-end'>
        <Button priority='primary' type='submit' loading={postMediaFile.isPending} disabled={postMediaFile.isPending}>
          <UploadIcon iconSize='s' />
          Upload file
        </Button>
      </div>
    </form>
  )

  const renderEmptyView = () => (
    <div className='flex w-full flex-col gap-sp-4'>
      <div className='flex flex-col gap-sp-1'>
        <div className='flex items-center gap-sp-1'>
          <StarIcon iconSize='xs' />
          <Paragraph size='xs' className='font-semibold text-text-dark'>
            Status
          </Paragraph>
          <span className='ml-sp-1 inline-flex items-center gap-sp-0.5 rounded-full bg-surface-error px-sp-0.75 py-sp-0.25 text-text-error'>
            <FileXmarkIcon iconSize='xs' />
            <Paragraph size='xs' className='font-semibold text-text-error'>
              None on file
            </Paragraph>
          </span>
        </div>
        <div className='flex items-center gap-sp-1'>
          <UploadIcon iconSize='xs' />
          <Paragraph size='xs' className='font-semibold text-text-dark'>
            Uploaded
          </Paragraph>
          <Paragraph size='xs' className='text-text-light'>
            -
          </Paragraph>
        </div>
      </div>
      <DrugScreenRequirements
        stateName={stateName}
        stateRulesHtml={stateRules}
        isLoading={Boolean(isRequirementsLoading)}
      />
      {onStartUpload ?
        <div className='mt-sp-2 flex justify-end'>
          <Button priority='primary' onClick={onStartUpload}>
            <UploadIcon iconSize='s' />
            Upload a drug screen
          </Button>
        </div>
      : null}
    </div>
  )

  return (
    <Popup
      onClose={onClose}
      isOpen
      showCloseIcon
      styles={{ wrapperClassName: 'w-[752px] [&>*]:!w-full max-h-[752px]' }}
      header={{
        content:
          view === 'upload' ?
            <div className='flex items-center gap-sp-1'>
              <UploadIcon iconSize='l' />
              <Heading level='h3'>Upload file</Heading>
            </div>
          : <div className='flex items-center gap-sp-1'>
              <VialCircleCheckIcon iconSize='l' />
              <Heading level='h3'>Drug Screen</Heading>{' '}
            </div>,
      }}
      body={
        <>
          {view === 'onFile' ?
            renderOnFileView()
          : view === 'upload' ?
            renderUploadView()
          : renderEmptyView()}
          <input
            ref={fileInputRef}
            type='file'
            data-testid='drug-screen-file-input'
            accept={ALLOWED_FILE_EXTENSIONS_STR}
            className='hidden'
            onChange={onFileChosen}
          />
        </>
      }
    />
  )
}

export default DrugScreenModal
